import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchRefundById,
  clearSelectedEntity,
  selectSelectedRefund,
  selectRefundDetailStatus,
  selectRefundDetailError,
} from '../../features/refunds/refundSlice';
import { selectUserInformations } from '../../features/authSlice';
import { InputField } from '../../components/reusable/InputField';
import refundFields from '../../config/refundFields';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';
import { FiEdit, FiArrowLeft } from 'react-icons/fi';

/**
 * Preview Refund Page
 * Full page read-only view of refund details
 */
const PreviewRefundPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedRefund = useSelector(selectSelectedRefund);
  const detailStatus = useSelector(selectRefundDetailStatus);
  const detailError = useSelector(selectRefundDetailError);

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchRefundById({ token: user.token, refundId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/refunds', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/refunds')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Refunds
          </button>
        </div>
      </div>
    );
  }

  if (!selectedRefund) {
    return null;
  }

  const textFields = refundFields.filter(field => field.type === 'text');
  const textareaField = refundFields.find(field => field.type === 'textarea');
  const selectField = refundFields.find(field => field.type === 'select');

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/refunds"
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  Refund Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View refund information
                </p>
              </div>
            </div>
            <Link
              to={`/dashboard/refunds/${id}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit className="w-4 h-4" />
              <span>Edit Refund</span>
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {textFields.map((field, index) => (
              <motion.div
                key={field.name}
                variants={itemVariants}
                transition={{ delay: index * 0.05 }}
              >
                <InputField
                  label={field.label}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={selectedRefund[field.name] || selectedRefund[field.value] || ''}
                  disabled={true}
                />
              </motion.div>
            ))}

            {selectField && (
              <motion.div variants={itemVariants}>
                <InputField
                  label={selectField.label}
                  name={selectField.name}
                  placeholder={selectField.placeholder}
                  value={selectedRefund[selectField.name] || ''}
                  disabled={true}
                />
              </motion.div>
            )}

            {textareaField && (
              <motion.div
                key={textareaField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="sm:col-span-2"
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {textareaField.label}
                </label>
                <textarea
                  name={textareaField.name}
                  className="w-full h-32 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none overflow-y-auto dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={textareaField.placeholder}
                  value={selectedRefund[textareaField.name] || ''}
                  disabled={true}
                />
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PreviewRefundPage;
